/**
 * docs_viewer_controller.js — Documentation viewer page (Stimulus).
 *
 * Ports the former static/js/docs_viewer.js attachOnce() page-init to an
 * element-scoped controller hosted on #docs-viewer-page (the .docs-layout root),
 * which already carries the API URLs (data-view-docs-url, data-search-docs-url).
 *
 * The page has three concerns:
 *   1. Sidebar tab switching (Files / Outline) + outline anchor smooth-scroll —
 *      document-delegated click handlers (the controls live inside this.element
 *      but the original delegated on document, so we preserve that).
 *   2. Per-page init: file-search wiring, auto-expanding folders for the active
 *      file, and the scroll-following copy button position.
 *   3. Stateless helpers toggleFolder()/copyContent() invoked by template
 *      data-action="call-fn" dispatch — these stay module-level and window-
 *      exposed exactly as before.
 *
 * Lifecycle (replaces the old attachOnce() once-guard):
 *   connect() binds the delegated document click listener, the scroll listener,
 *   and runs the per-page init; disconnect() removes those same listeners.
 *   Because the controller instance owns add+remove, the listeners cannot stack
 *   across Turbo navigations — the exact failure attachOnce() was hand-rolling
 *   around. Stimulus re-runs connect() on each navigation, so the per-page init
 *   that the original ran on turbo:load runs here directly (no turbo:load
 *   subscription needed: the element's presence IS the per-page signal).
 *
 * Template: templates/docs/viewer.html
 */

import { Controller } from "@hotwired/stimulus";

    // ---- stateless helpers (template data-action="call-fn" dispatch) --------

    function toggleFolder(btn) {
        btn.classList.toggle('open');
        var ul = btn.nextElementSibling;
        if (ul) ul.classList.toggle('expanded');
    }

    function copyContent() {
        var dataEl = document.getElementById('docs-raw-content');
        if (!dataEl) return;
        var rawContent = JSON.parse(dataEl.textContent);
        navigator.clipboard.writeText(rawContent).then(function () {
            var btn = document.querySelector('.copy-btn');
            btn.innerHTML = '<i class="bi bi-check"></i> Copied!';
            setTimeout(function () {
                btn.innerHTML = '<i class="bi bi-clipboard"></i> Copy';
            }, 2000);
        });
    }

    function initFileSearch(page) {
        var searchInput = document.getElementById('file-search');
        var clearBtn = document.getElementById('search-clear');
        var fileTree = document.getElementById('file-tree');
        var searchResults = document.getElementById('search-results');

        if (!searchInput || !fileTree || !searchResults) return;

        var baseUrl = page.dataset.viewDocsUrl || '';
        var searchUrl = page.dataset.searchDocsUrl || '';
        var debounceTimer = null;
        var currentQuery = '';

        function expandToElement(el) {
            var parent = el.parentElement;
            while (parent && !parent.classList.contains('file-tree')) {
                if (parent.tagName === 'UL') {
                    parent.classList.add('expanded');
                    var toggle = parent.previousElementSibling;
                    if (toggle && toggle.classList.contains('folder-toggle')) {
                        toggle.classList.add('open');
                    }
                }
                parent = parent.parentElement;
            }
        }

        function resetView() {
            fileTree.style.display = '';
            searchResults.style.display = 'none';
            searchResults.innerHTML = '';
            clearBtn.style.display = 'none';

            fileTree.querySelectorAll('ul').forEach(function (ul) { ul.classList.remove('expanded'); });
            fileTree.querySelectorAll('.folder-toggle').forEach(function (btn) { btn.classList.remove('open'); });
            var activeLink = fileTree.querySelector('a.active');
            if (activeLink) expandToElement(activeLink);
        }

        function highlightMatch(text, query) {
            if (!query) return text;
            var regex = new RegExp('(' + query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + ')', 'gi');
            return text.replace(regex, '<mark>$1</mark>');
        }

        function escapeHtml(text) {
            var div = document.createElement('div');
            div.textContent = text;
            return div.innerHTML;
        }

        function renderResults(results, query) {
            if (results.length === 0) {
                searchResults.innerHTML = '<div class="search-no-results">No matches found</div>';
                return;
            }

            var html = results.map(function (result) {
                var snippetsHtml = result.content_matches.map(function (match) {
                    return '<div class="search-snippet"><span class="line-num">L' + match.line + '</span>' + highlightMatch(escapeHtml(match.snippet), query) + '</div>';
                }).join('');

                return '<div class="search-result-item" data-href="' + baseUrl + result.path + '">' +
                    '<div class="result-title"><i class="bi bi-file-text"></i> ' + highlightMatch(escapeHtml(result.name), query) + '</div>' +
                    (snippetsHtml ? '<div class="search-result-snippets">' + snippetsHtml + '</div>' : '') +
                    '</div>';
            }).join('');

            searchResults.innerHTML = html;

            searchResults.querySelectorAll('.search-result-item').forEach(function (item) {
                item.addEventListener('click', function () {
                    Turbo.visit(this.dataset.href);
                });
            });
        }

        function performSearch(query) {
            query = query.trim();
            currentQuery = query;

            if (query.length < 2) {
                resetView();
                return;
            }

            clearBtn.style.display = 'block';
            fileTree.style.display = 'none';
            searchResults.style.display = 'block';
            searchResults.innerHTML = '<div class="search-loading"><i class="bi bi-hourglass-split"></i> Searching...</div>';

            window.api(searchUrl + '?q=' + encodeURIComponent(query), {
                notifyError: false
            })
                .then(function (data) {
                    if (query === currentQuery) {
                        renderResults(data.results, query);
                    }
                })
                .catch(function (error) {
                    console.error('Search error:', error);
                    searchResults.innerHTML = '<div class="search-no-results">Search error</div>';
                });
        }

        searchInput.addEventListener('input', function () {
            clearTimeout(debounceTimer);
            var query = this.value;
            if (query.trim().length < 2) {
                resetView();
                return;
            }
            debounceTimer = setTimeout(function () { performSearch(query); }, 200);
        });

        clearBtn.addEventListener('click', function () {
            searchInput.value = '';
            currentQuery = '';
            clearTimeout(debounceTimer);
            resetView();
            searchInput.focus();
        });

        searchInput.addEventListener('keydown', function (e) {
            if (e.key === 'Escape') {
                searchInput.value = '';
                currentQuery = '';
                clearTimeout(debounceTimer);
                resetView();
            }
        });
    }

    // Expose for template dispatcher (data-action="call-fn" in global-actions.js).
    window.copyContent = copyContent;
    window.toggleFolder = toggleFolder;

export default class extends Controller {
        connect() {
            // Delegated on document (preserves the original attachOnce wiring).
            // The controller instance owns add/remove, so handlers don't stack
            // across navigations (the attachOnce replacement).
            this._onClick = this.onDocumentClick.bind(this);
            document.addEventListener('click', this._onClick);

            // Per-page init the original ran on turbo:load. The element's
            // presence IS the per-page signal, so run it directly on connect.
            this.initPage();
        }

        disconnect() {
            if (this._onClick) {
                document.removeEventListener('click', this._onClick);
                this._onClick = null;
            }
            if (this._onScroll) {
                window.removeEventListener('scroll', this._onScroll);
                this._onScroll = null;
            }
        }

        onDocumentClick(e) {
            var tabBtn = e.target.closest('.sidebar-toggle button');
            if (tabBtn) {
                document.querySelectorAll('.sidebar-toggle button').forEach(function (b) { b.classList.remove('active'); });
                document.querySelectorAll('.sidebar-panel').forEach(function (p) { p.classList.remove('active'); });
                tabBtn.classList.add('active');
                var panel = document.getElementById(tabBtn.dataset.panel + '-panel');
                if (panel) panel.classList.add('active');
                return;
            }

            var outlineLink = e.target.closest('.outline-list a');
            if (outlineLink) {
                e.preventDefault();
                var targetId = outlineLink.getAttribute('href').substring(1);
                var target = document.getElementById(targetId);
                if (target) {
                    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }
        }

        initPage() {
            var page = document.getElementById('docs-viewer-page');
            if (!page) return;

            initFileSearch(page);

            // Auto-expand folders containing current file
            var activeLink = document.querySelector('.file-tree a.active');
            if (activeLink) {
                var parent = activeLink.parentElement;
                while (parent && !parent.classList.contains('file-tree')) {
                    if (parent.tagName === 'UL') {
                        parent.classList.add('expanded');
                        var toggle = parent.previousElementSibling;
                        if (toggle && toggle.classList.contains('folder-toggle')) {
                            toggle.classList.add('open');
                        }
                    }
                    parent = parent.parentElement;
                }
            }

            // Adjust copy button position on scroll
            var copyBtn = document.querySelector('.copy-btn');
            var navbar = document.getElementById('main-navbar');
            if (copyBtn && navbar) {
                var navbarHeight = navbar.offsetHeight;
                var gap = 20;
                var initialTop = navbarHeight + gap;
                copyBtn.style.top = initialTop + 'px';

                this._onScroll = function () {
                    if (window.scrollY >= navbarHeight) {
                        copyBtn.style.top = gap + 'px';
                    } else {
                        copyBtn.style.top = (initialTop - window.scrollY) + 'px';
                    }
                };
                window.addEventListener('scroll', this._onScroll, { passive: true });
            }
        }
    }
